import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  TextField,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { dateLocale, useLocalize } from "../i18n";
import { toast } from "react-toastify";
import { setFieldsLayerKey, setTiles } from "../reducers/MapSlice";
import { newId } from "../utils/id";
import {
  getAlertsDashboard,
  getFieldsDashboard,
  reviewedAlerts,
  validateAlerts,
  getIndexTile,
} from "../api/FlaskAPI";
import { alertKeys, fieldKeys } from "../api/queryKeys";
import { toggleSx } from "./BaseMapToggle";
import { NameWithId } from "./Dashboard/TableCells";

const ALERT_TYPES = ["unreviewed", "reviewed", "invalid", "all"];
const EMPTY_COUNTS = { all: 0, unreviewed: 0, reviewed: 0, invalid: 0 };

const columns = [
  { key: "field_id", sortable: true, type: "text" },
  { key: "date", sortable: true, type: "date" },
  { key: "index_name_en", sortable: true, type: "text" },
  { key: "metric_en", sortable: true, type: "text" },
  { key: "percentage", sortable: true, type: "number" },
  { key: "created_at", sortable: true, type: "date" },
  { key: "updated_at", sortable: true, type: "date" },
];

const ALERT_TYPE_BUTTONS = {
  all: [],
  unreviewed: [
    {
      color: "success",
      action: "reviewed",
      Icon: CheckCircleOutlineIcon,
    },
    {
      color: "error",
      action: "invalidate",
      Icon: BlockOutlinedIcon,
    },
  ],
  reviewed: [
    {
      color: "inherit",
      action: "unreviewed",
      Icon: UndoOutlinedIcon,
      flip: true, // undo turns round in Arabic
    },
  ],
  invalid: [
    {
      color: "success",
      action: "validate",
      Icon: RestoreOutlinedIcon,
    },
  ],
};

// "2026-10-03 09:41:41" to "3 Oct 26, 09:41"
const formatStamp = (value, language) => {
  const date = value ? new Date(value.replace(" ", "T")) : null;
  if (!date || Number.isNaN(date.getTime())) return value || "-";
  const locale = dateLocale(language);
  return `${date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "2-digit",
  })}, ${date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })}`;
};

const formatDay = (ymd, language) =>
  ymd
    ? new Date(`${ymd}T00:00:00`).toLocaleDateString(dateLocale(language), {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "-";

export default function AlertsTable({ fieldId = null, onAlertsChanged }) {
  const selectedColumns = fieldId
    ? columns.filter((c) => c.key !== "field_id")
    : columns;
  const [alertType, setAlertType] = useState("unreviewed");
  // id -> alert. Kept across pages, so it holds the full row: "add to
  // workspace" needs each alert's index and date, not just its id.
  const [selectedAlerts, setSelectedAlerts] = useState(new Map());
  const [alertActionLoading, setAlertActionLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination state (page is 0-based for TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const language = i18n.resolvedLanguage;
  const loadErrorMessage = t("alertsTable.loadFailed");
  const workspaces = useSelector((state) => state.mapState.workspaces);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const mapBounds = useSelector((state) => state.mapState.mapBounds);
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);

  const params = useMemo(() => {
    const params = {
      field_id: fieldId,
      status: alertType,
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (sortConfig.key) {
      params.sort_by = sortConfig.key;
      params.sort_dir = sortConfig.direction;
    }
    return params;
  }, [fieldId, alertType, page, rowsPerPage, debouncedSearch, sortConfig]);

  // keepPreviousData leaves the current rows on screen while the next page,
  // tab, search or sort loads, and while a review click refetches.
  const {
    data,
    isPending: loading,
    isError,
    isFetching,
    isPlaceholderData,
  } = useQuery({
    queryKey: alertKeys.list(params),
    queryFn: ({ signal }) =>
      getAlertsDashboard(params, signal).then((res) => res.data),
    placeholderData: keepPreviousData,
    meta: { errorMessage: loadErrorMessage },
  });
  const alerts = data?.items || [];
  const totalCount = data?.total || 0;
  const counts = data?.counts || EMPTY_COUNTS;
  const error = isError ? loadErrorMessage : null;

  const { data: allFields } = useQuery({
    queryKey: fieldKeys.list({}),
    queryFn: ({ signal }) =>
      getFieldsDashboard({}, signal).then((res) => res.data),
    enabled: !fieldId,
  });
  const ownerByField = useMemo(
    () => new Map((allFields?.items || []).map((f) => [f.id, f.owner])),
    [allFields],
  );

  // If the current page has no results, go back one page. (happen when the user invalidates or reviews the last alert on a page, or change the search query).
  useEffect(() => {
    if (data && !isPlaceholderData && page > 0 && data.items?.length === 0) {
      setPage((prev) => Math.max(0, prev - 1));
    }
  }, [data, isPlaceholderData, page]);

  // Debounce the search box so each keystroke doesn't hit the backend
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
    setPage(0);
    setSelectedAlerts(new Map());
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  const handleTypeSelect = (type) => {
    if (!type) return;
    setAlertType(type);
    setSelectedAlerts(new Map());
    setSortConfig({ key: null, direction: null });
    setPage(0);
  };

  // Selection handlers
  const toggleAlertSelection = (alert) => {
    setSelectedAlerts((prev) => {
      const next = new Map(prev);
      if (next.has(alert.id)) {
        next.delete(alert.id);
      } else {
        next.set(alert.id, alert);
      }
      return next;
    });
  };

  // The header checkbox selects all alerts on the current page, not all pages. The user can then select more on other pages.
  const selectedOnPage = alerts.filter((alert) =>
    selectedAlerts.has(alert.id),
  ).length;
  const allSelected = alerts.length > 0 && selectedOnPage === alerts.length;
  const selectedOffPage = selectedAlerts.size - selectedOnPage;

  const toggleSelectAll = () => {
    setSelectedAlerts((prev) => {
      const next = new Map(prev);
      alerts.forEach((alert) =>
        allSelected ? next.delete(alert.id) : next.set(alert.id, alert),
      );
      return next;
    });
  };

  const clearSelection = () => setSelectedAlerts(new Map());

  // Action handlers
  const handleAlertAction = async (action) => {
    if (selectedAlerts.size === 0) {
      toast.error(t("alertsTable.selectAtLeastOne"));
      return;
    }

    const alertIds = [...selectedAlerts.keys()];
    setAlertActionLoading(true);

    try {
      if (action === "reviewed") {
        await reviewedAlerts({ alerts_ids: alertIds, reviewed: true });
      } else if (action === "unreviewed") {
        await reviewedAlerts({ alerts_ids: alertIds, reviewed: false });
      } else if (action === "invalidate") {
        await validateAlerts({ alerts_ids: alertIds, valid: false });
      } else if (action === "validate") {
        await validateAlerts({ alerts_ids: alertIds, valid: true });
      } else {
        throw new Error("Unsupported action");
      }

      setSelectedAlerts(new Map());
      // Field rows show whether a field has open alerts.
      // Invalidating alerts also refreshes the panel behind this dialog.
      queryClient.invalidateQueries({ queryKey: fieldKeys.all });
      await queryClient.invalidateQueries({ queryKey: alertKeys.all });
      if (onAlertsChanged) {
        await onAlertsChanged();
      }
      dispatch(setFieldsLayerKey());
    } catch (error) {
      console.error("Error updating alerts:", error);
      toast.error(t("panel.alertsFailed"));
    } finally {
      setAlertActionLoading(false);
    }
  };

  const handleAddAlertToWorkspace = async () => {
    if (selectedAlerts.size === 0) {
      toast.error(t("alertsTable.selectAtLeastOne"));
      return;
    }

    const alertsToAdd = [...selectedAlerts.values()];

    // Check tile limit for current workspace before adding
    if (
      workspaces[currentWorkspace].length + alertsToAdd.length >
      tilesLimits.workspace_tiles
    ) {
      toast.error(
        t("map.maxTiles", {
          max: tilesLimits.workspace_tiles,
          workspace: currentWorkspace,
        }),
      );
      return;
    }

    setAlertActionLoading(true);

    try {
      for (const alert of alertsToAdd) {
        const tileParams = {
          index_id: alert.index_id,
          image_date: alert.date,
          bounds: mapBounds,
        };
        const res = await getIndexTile(tileParams);

        if (res.status === 200) {
          dispatch(
            setTiles({
              tileId: newId(),
              tileUrl: res.data.tile_url_template,
              bbox: res.data.bounds,
              tileName: res.data.imagename_en,
              tileUrlExpiresAt: res.data.expires_at,
              // Kept so the signed tile URL can be re-minted before it expires.
              tileParams: tileParams,
            }),
          );
        }
      }
      toast.success(t("alertsTable.added"));
    } catch (error) {
      console.error("Error adding alert tile to workspace:", error);
      toast.error(t("panel.addTileFailed"));
    } finally {
      setAlertActionLoading(false);
    }
  };

  // Render cell content based on column type
  const renderCellContent = (alert, column) => {
    const value = alert[column.key];

    if (column.key === "field_id") {
      return <NameWithId name={ownerByField.get(value)} id={value} />;
    }
    if (column.key === "date") return formatDay(value, language);
    if (column.key === "created_at" || column.key === "updated_at") {
      return formatStamp(value, language);
    }
    if (column.key === "index_name_en") return localize(alert, "index_name");
    if (column.key === "metric_en") return localize(alert, "metric");

    // Percentage column - round to 2 decimals
    if (column.key === "percentage") {
      return value !== undefined && value !== null
        ? `${Number(value).toFixed(2)}%`
        : "-";
    }

    // Default - just show the value
    return value || "-";
  };

  return (
    <>
      {/* Controls Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={alertType}
          onChange={(_, type) => handleTypeSelect(type)}
          aria-label={t("fieldAlerts.statusLabel")}
          sx={[
            toggleSx,
            {
              bgcolor: "white",
              p: 0.5,
              borderRadius: "10px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              "& .MuiToggleButton-root": { px: 1.5, py: 0.5, fontSize: 13 },
            },
          ]}
        >
          {ALERT_TYPES.map((type) => (
            <ToggleButton key={type} value={type}>
              {type === "all"
                ? t("alertsTable.all")
                : t(`fieldAlerts.status.${type}`)}
              <Box component="span" sx={{ ml: 0.75, opacity: 0.7 }}>
                {counts[type] ?? 0}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Search */}
        {!fieldId && (
          <TextField
            placeholder={t("dashboard.searchAlerts")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Picked rows may not match the new search.
              setSelectedAlerts(new Map());
            }}
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 300 },
              backgroundColor: "white",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          minHeight: 40,
          px: 1.5,
          mb: 1,
          borderRadius: "8px",
          bgcolor:
            selectedAlerts.size > 0
              ? "rgba(var(--primary-rgb), 0.08)"
              : "transparent",
        }}
      >
        {selectedAlerts.size > 0 ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, mr: "auto" }}>
              {t("fieldAlerts.selected", { count: selectedAlerts.size })}
              {selectedOffPage > 0 &&
                t("alertsTable.otherPages", { count: selectedOffPage })}
            </Typography>
            <Button
              size="small"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
              disabled={alertActionLoading}
              onClick={handleAddAlertToWorkspace}
              sx={{ textTransform: "none" }}
            >
              {t("fieldAlerts.showOnMap")}
            </Button>
            {ALERT_TYPE_BUTTONS[alertType].map(
              ({ action, color, Icon, flip }) => (
                <Button
                  key={action}
                  size="small"
                  color={color}
                  startIcon={<Icon className={flip ? "flip-rtl" : undefined} />}
                  disabled={alertActionLoading}
                  onClick={() => handleAlertAction(action)}
                  sx={{ textTransform: "none" }}
                >
                  {t(`fieldAlerts.actions.${action}`)}
                </Button>
              ),
            )}
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseIcon />}
              disabled={alertActionLoading}
              onClick={clearSelection}
              sx={{ textTransform: "none" }}
            >
              {t("common.clear")}
            </Button>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            {t(`alertsTable.hints.${alertType}`, {
              shown: alerts.length,
              total: totalCount,
            })}
          </Typography>
        )}
      </Box>

      {/* Reserves the bar's height so the table doesn't jump when it appears. */}
      <Box sx={{ height: 4, mb: 1 }}>
        {isFetching && !loading && <LinearProgress />}
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && alerts.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
            {t("alertsTable.noneFound")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {t(`fieldAlerts.none.${alertType}`)}
          </Typography>
        </Paper>
      )}

      {/* Table */}
      {!loading && !error && alerts.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedOnPage > 0 && !allSelected}
                      onChange={toggleSelectAll}
                      disabled={alertActionLoading}
                    />
                  </TableCell>
                  {selectedColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        cursor: column.sortable ? "pointer" : "default",
                        userSelect: "none",
                        "&:hover": column.sortable
                          ? { backgroundColor: "#e5e7eb" }
                          : {},
                      }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {t(`alertsTable.columns.${column.key}`)}
                        {column.sortable && getSortIcon(column.key)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert, index) => {
                  const is_selected = selectedAlerts.has(alert.id);
                  return (
                    <TableRow
                      key={alert.id || index}
                      onClick={() => toggleAlertSelection(alert)}
                      sx={{
                        backgroundColor: is_selected
                          ? "#e3f2fd"
                          : index % 2 === 0
                            ? "white"
                            : "#fafafa",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#e8f4f8" },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedAlerts.has(alert.id)}
                          onChange={() => toggleAlertSelection(alert)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={alertActionLoading}
                        />
                      </TableCell>
                      {selectedColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align || "left"}
                          sx={{
                            whiteSpace: "nowrap",
                            color: "#4b5563",
                          }}
                        >
                          {renderCellContent(alert, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </>
  );
}
